// LA GRILLE DE GLYPHS, sans GPU : les rampes de densité, le choix du glyphe de
// contour selon la direction du gradient (reproduit tel que le shader le
// calcule), les tailles de grille selon l'état musical, la vague de
// « déroulement » au changement de scène, l'ordre des scènes sources (R20 : la
// pochette ou le titre lisible au moins une fois par cycle).

// la rampe de densité : du vide au bloc plein (16, et 10 au tier 0)
export const RAMP16 = [' ', '.', '\'', ':', ';', '-', '=', '+', '*', 'x', 'o', 'X', '#', '%', '@', '█'];
export const RAMP10 = [' ', '.', ':', '-', '=', '+', '*', '#', '@', '█'];
// les glyphes de contour, dans l'ordre de l'atlas après la rampe
export const EDGE = [')', '>', '\\', '-', '/', '<', '(', '_'];
// le glyphe de contour par secteur de 22,5° de la direction du GRADIENT (vers le
// clair), indices dans EDGE : un contour vertical au clair à droite est « ) »,
// au clair à gauche « ( » ; le clair en haut donne « - », en bas « _ » ; les
// diagonales « / » et « \ » sont PERPENDICULAIRES au gradient ; « < » et « > »
// aux demi-secteurs proches de la verticale
export const EDGE_BY_BIN = [0, 1, 2, 3, 3, 3, 4, 5, 6, 5, 2, 7, 7, 7, 4, 1];
// le seuil de contour (norme du gradient de luminance entre cellules voisines)
export const EDGE_THRESHOLD = 0.18;

// le secteur du gradient (gx, gy : y vers le haut), 16 secteurs de 22,5°
export function edgeBin(gx, gy) {
  const a = Math.atan2(gy, gx);                                // [-π, π]
  return ((Math.round(a / (Math.PI / 8)) % 16) + 16) % 16;
}
export function edgeGlyph(gx, gy) { return EDGE[EDGE_BY_BIN[edgeBin(gx, gy)]]; }
// l'indice de rampe d'une luminance (comme le shader : arrondi au plus proche)
export function rampIndex(lum, n) { return Math.max(0, Math.min(n - 1, Math.round(Math.max(0, Math.min(1, lum)) * (n - 1)))); }

// LES GRILLES : colonnes x lignes selon l'état ; le tier 0 part de 120 x 68
export const GRIDS = { normal: [160, 90], massive: [120, 68], fine: [220, 124], drop: [64, 36] };
export function gridFor(state, tier) {
  if (state === 'normal' && tier === 0) return GRIDS.massive;
  return GRIDS[state] || GRIDS.normal;
}
// l'état de grille : le drop prime (une mesure), puis la montée, puis la mesure forte
export function gridState({ dropBarsLeft, building, strongBar }) {
  if (dropBarsLeft > 0) return 'drop';
  if (building) return 'fine';
  if (strongBar) return 'massive';
  return 'normal';
}

// LE DÉROULEMENT au changement de scène : une vague traverse la grille ; chaque
// cellule, atteinte au temps delay, fait défiler ROLL_STEPS glyphes au hasard
// pendant ROLL_DUR puis montre la nouvelle scène. Comme le shader.
export const ROLL_SPAN = 0.7, ROLL_DUR = 0.35, ROLL_STEPS = 4;
export function hash2(x, y) { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }
// u, v : la cellule en [0, 1] ; dir : la direction de la vague (unitaire) ; t : secondes depuis le début
export function rollState(u, v, dirX, dirY, t) {
  const ext = (Math.abs(dirX) + Math.abs(dirY)) * 0.5;         // la demi-étendue de la grille projetée sur la vague
  const along = (((u - 0.5) * dirX + (v - 0.5) * dirY) / ext + 1) * 0.5;   // [0, 1] dans le sens de la vague
  const delay = along * ROLL_SPAN + hash2(u * 97, v * 57) * 0.08;
  const p = (t - delay) / ROLL_DUR;
  if (p <= 0) return { phase: 0, step: -1 };                   // l'ancienne scène
  if (p >= 1) return { phase: 1, step: -1 };                   // la nouvelle
  return { phase: p, step: Math.floor(p * ROLL_STEPS) };       // en train de dérouler (step : quel glyphe aléatoire)
}
export const ROLL_TOTAL = ROLL_SPAN + 0.08 + ROLL_DUR;

// LES SCÈNES SOURCES et leur ordre : jamais deux fois la même de suite ; quand
// un cycle (R20) commence sans qu'on ait vu la pochette ou le titre, la
// prochaine est la pochette (ou le titre sans pochette)
export const SCENES = ['tunnel', 'sphere', 'cover', 'waves', 'title'];
export function nextScene(current, hasCover, mustAnchor, rnd) {
  if (mustAnchor) { const a = hasCover ? 'cover' : 'title'; if (a !== current) return a; }
  const pool = SCENES.filter((s) => s !== current && (hasCover || s !== 'cover'));
  return pool[Math.min(pool.length - 1, Math.floor((rnd || 0) * pool.length))];
}
export const MODES = ['color', 'terminal', 'print'];
